/**
 * Projects Service
 * 
 * @swagger-tag Projects
 * @description API controller for managing projects.
 */
import ApiService from './api';
import type { components } from '@/core/types/generated/api';
import type { SieveQuery } from '@/shared/utils/sieve';

// ProjectService (Clean Architecture) - Returns DTOs only, accepts Sieve queries only
// Mapping is handled in stores using ProjectMapper

export interface ProjectListParams extends SieveQuery {
  /** Whether to include team members */
  includeMembers?: boolean;
  /** Project ids to exclude from the results (e.g. ones already pinned in a "recent" block) */
  excludeIds?: string[];
}

export interface ProjectGetAllParams {
  /** Whether to include only template projects */
  templatesOnly?: boolean;
  /** Whether to include team member details */
  includeTeamMembers?: boolean;
}

export interface ProjectGetByIdParams {
  /** Whether to include team member details */
  includeTeamMembers?: boolean;
}

type ProjectDto = components['schemas']['ProjectDto'];
type ProjectDtoPaginatedResponse = components['schemas']['ProjectDtoPaginatedResponse'];
type CreateProjectCommand = components['schemas']['CreateProjectCommand'];
type UpdateProjectCommand = components['schemas']['UpdateProjectCommand'];

export const ProjectService = {
  async getProjects(params: ProjectListParams = {}): Promise<ProjectDtoPaginatedResponse> {
    const response = await ApiService.get<ProjectDtoPaginatedResponse>('/Projects', { params });
    return response.data;
  },

  async getAllProjects(params: ProjectGetAllParams = {}): Promise<ProjectDto[]> {
    const response = await ApiService.get<ProjectDto[]>('/Projects/all', { params });
    return response.data || [];
  },

  async getMyProjects(params: ProjectListParams = {}): Promise<ProjectDtoPaginatedResponse> {
    const response = await ApiService.get<ProjectDtoPaginatedResponse>('/Projects/my-projects', { params });
    return response.data;
  },

  async getProject(id: string, params: ProjectGetByIdParams = {}): Promise<ProjectDto> {
    const response = await ApiService.get<ProjectDto>(`/Projects/${id}`, { params });
    return response.data;
  },

  async createProject(body: CreateProjectCommand): Promise<ProjectDto> {
    const response = await ApiService.post<ProjectDto>('/Projects', body);
    return response.data;
  },

  async updateProject(id: string, body: UpdateProjectCommand): Promise<ProjectDto> {
    const response = await ApiService.put<ProjectDto>(`/Projects/${id}`, body);
    return response.data;
  },

  async deleteProject(id: string): Promise<void> {
    await ApiService.delete(`/Projects/${id}`);
  },

  async getProjectTemplates(): Promise<ProjectDto[]> {
    const response = await ApiService.get<ProjectDto[]>('/Projects/templates');
    return response.data || [];
  },

  async getMyRecentProjects(): Promise<ProjectDto[]> {
    const response = await ApiService.get<ProjectDto[]>('/Projects/my-recent-projects');
    return response.data || [];
  },

  async recordSelection(id: string): Promise<void> {
    await ApiService.post(`/Projects/${id}/select`);
  },
};
